// Mirror of the YouTube rules in backend/store_rules.py.
// The backend is the source of truth for what gets stored and what the product
// page renders; this copy exists so the admin product dialog can validate and
// preview a pasted link before saving.

const YOUTUBE_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
];

const YOUTU_BE_HOSTS = ['youtu.be', 'www.youtu.be'];

const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;
const ID_PATH_PREFIXES = ['/embed/', '/shorts/', '/live/', '/v/'];
const TIMESTAMP_RE = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/i;
const MAX_START_SECONDS = 24 * 60 * 60;

const parse = (url) => {
  if (!url || typeof url !== 'string') return null;
  let raw = url.trim();
  if (!raw) return null;
  if (!raw.includes('//')) raw = `https://${raw.replace(/^\/+/, '')}`;
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed;
  } catch {
    return null;
  }
};

export const youtubeVideoId = (url) => {
  const parsed = parse(url);
  if (!parsed) return null;
  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname || '';
  let candidate = null;

  if (YOUTU_BE_HOSTS.includes(host)) {
    candidate = path.replace(/^\/+/, '').split('/')[0];
  } else if (YOUTUBE_HOSTS.includes(host)) {
    if (path.replace(/\/+$/, '') === '/watch') {
      candidate = parsed.searchParams.get('v');
    } else {
      const prefix = ID_PATH_PREFIXES.find((p) => path.startsWith(p));
      if (prefix) candidate = path.slice(prefix.length).split('/')[0];
    }
  }

  if (!candidate || !VIDEO_ID_RE.test(candidate)) return null;
  return candidate;
};

export const youtubeStartSeconds = (url) => {
  const parsed = parse(url);
  if (!parsed) return null;
  for (const key of ['t', 'start']) {
    const value = parsed.searchParams.get(key);
    if (!value) continue;
    const match = TIMESTAMP_RE.exec(value.trim());
    if (!match || !match.slice(1).some(Boolean)) continue;
    const total =
      parseInt(match[1] || 0, 10) * 3600 +
      parseInt(match[2] || 0, 10) * 60 +
      parseInt(match[3] || 0, 10);
    if (total > 0 && total <= MAX_START_SECONDS) return total;
  }
  return null;
};

export const youtubeEmbedUrl = (url) => {
  const videoId = youtubeVideoId(url);
  if (!videoId) return null;
  const start = youtubeStartSeconds(url);
  const base = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`;
  return start ? `${base}&start=${start}` : base;
};

export const youtubeThumbnailUrl = (url) => {
  const videoId = youtubeVideoId(url);
  return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : null;
};

// true when the field is blank (the link is optional) or holds a real YouTube URL.
export const isAcceptableYoutubeValue = (url) => {
  if (!url || !url.trim()) return true;
  return youtubeVideoId(url) !== null;
};
